import { flow } from "mobx";
import { consoleError, consoleLog, consoleWarn } from "./AppUtils";
import { fetch } from "./FetchMiddlewareUtils";
import customerStore from "../stores/CustomerStore";

/**
 *
 * @param self
 * @param uri
 * @param success
 * @param fail
 * @param error
 * @returns {CancellablePromise<FlowReturnType<any>>}
 */
export const getRequest = (
  self,
  uri,
  success,
  fail = () => {},
  error = () => {}
) => {
  const promise = flow(function*() {
    self.state = "fetching";

    try {
      const response = yield fetch(uri);
      const body = response.body;
      if (!response || !body) {
        self.state = "error";
        error(`${uri}: No response`);
        return;
      }
      switch (body.status) {
        case "success":
          self.state = "done";
          consoleLog(uri, body.data);
          success(body.data);
          break;
        case "fail":
          self.state = "fail";
          consoleWarn(body);
          fail(body.data);
          break;
        case "error":
          self.state = "error";
          //TODO: add error page, and redirect error cases to there.
          consoleError(body);
          error(body);
          break;
        default:
      }
    } catch (error) {
      consoleError(error);
      //TODO: add error page, and redirect error cases to there.
      self.state = "error";
    }
  })();

  promise.catch(() => {
    consoleLog("promise cancelled", uri);
  });

  self.promise = promise;

  return promise;
};

/**
 *
 * @param self
 * @param uri
 * @param paramsBody
 * @param success
 * @param fail
 * @param error
 * @returns {CancellablePromise<FlowReturnType<any>>}
 */
export const postRequest = (
  self,
  uri,
  paramsBody = {},
  success,
  fail = () => {},
  error = () => {}
) => {
  const promise = flow(function*() {
    self.state = "fetching";

    try {
      const response = yield fetch(uri, {
        method: "post",
        body: JSON.stringify(paramsBody)
      });
      const body = response.body;
      if (!response || !body) {
        self.state = "error";
        return;
      }
      switch (body.status) {
        case "success":
          self.state = "done";
          consoleLog(uri, body.data);
          success(body.data);
          break;
        case "fail":
          self.state = "fail";
          consoleWarn(body);
          fail(body.data);
          break;
        case "error":
          self.state = "error";
          //TODO: add error page, and redirect error cases to there.
          consoleError(body);
          error(body.data);
          break;
        default:
      }
    } catch (e) {
      consoleError(e);
      //TODO: add error page, and redirect error cases to there.
      self.state = "error";
      error(e);
    }
  })();

  promise.catch(error => {
    consoleLog("promise cancelled", uri, error);
  });

  self.promise = promise;

  return promise;
};

/**
 *
 * @param self
 * @param request
 * @param success
 * @param error
 * @returns {CancellablePromise<FlowReturnType<any>>}
 */
export const searchRequest = (self, request, success, error = () => {}) => {
  const promise = flow(function*() {
    self.state = "fetching";

    try {
      const { query, page = 0, per = 30, category } = request;

      const params = new URLSearchParams({
        query: query,
        page: page.toString(),
        per: per.toString()
      });

      if (category) {
        params.append("category", category);
      }

      if (
        customerStore.vendorId &&
        customerStore.vendorId !== null &&
        customerStore.vendorId !== undefined
      ) {
        params.append("vendor_id", customerStore.vendorId.toString());
      }

      const response = yield fetch(`v15/search?${params.toString()}`);

      if (!response || !response.body) {
        self.state = "error";
        return;
      }

      const body = response.body;

      if (body.success === true) {
        self.state = "done";
        consoleLog("Search API response:", body.data);
        success(body.data, body.pagination);
      } else {
        self.state = "error";
        consoleError("Search API error:", body);
        error(body);
      }
    } catch (e) {
      self.state = "error";
      consoleError(e);
      error(e);
    }
  })();

  promise.catch(() => {
    consoleLog("promise cancelled");
  });

  self.promise = promise;

  return promise;
};

/**
 *
 * @param self
 * @param request
 * @param success
 * @param error
 * @returns {CancellablePromise<FlowReturnType<any>>}
 */
export const searchSuggestionRequest = (
  self,
  request,
  success,
  error = () => {}
) => {
  const promise = flow(function*() {
    self.state = "fetching";

    try {
      const { query, limit = 6 } = request;

      // Build query parameters for suggestions
      const params = new URLSearchParams({
        query: query,
        limit: limit.toString()
      });

      // Add vendor_id only if available
      if (
        customerStore.vendorId &&
        customerStore.vendorId !== null &&
        customerStore.vendorId !== undefined
      ) {
        params.append("vendor_id", customerStore.vendorId.toString());
      } else {
        consoleWarn(
          "Vendor ID not available for search suggestions, proceeding without vendor_id",
          {
            vendorId: customerStore.vendorId,
            isLoggedIn: customerStore.isLoggedIn
          }
        );
      }

      const response = yield fetch(
        `v15/search-suggestion?${params.toString()}`
      );

      if (!response || !response.body) {
        self.state = "error";
        return;
      }

      const body = response.body;

      if (body.success === true) {
        self.state = "done";
        consoleLog("Search suggestion API response:", body.data);
        success(body.data);
      } else {
        self.state = "error";
        consoleError("Search suggestion API error:", body);
        error(body);
      }
    } catch (e) {
      //TODO: add error page, and redirect error cases to there.
      self.state = "error";
      consoleError(e);
      error(e);
    }
  })();

  promise.catch(() => {
    consoleLog("promise cancelled");
  });

  self.promise = promise;

  return promise;
};
